/**
 * Trial Source Control Banner
 *
 * Shown to trial users after 5+ Evidence chats if they haven't used source control yet.
 * Encourages trying source control during the free trial. Renders above the chat input.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7548-31153
 */

import { useTrialSourceControlBanner } from '@/components/chat/hooks/useTrialSourceControlBanner';
import {
  Banner,
  BannerAction,
  BannerClose,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { TierProFilled } from '@heidi/design-system/icons';
import { cn } from '@/lib/cn';
import type { RefObject } from 'react';
import { useLayoutEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { trialSourceControlBannerMessages } from './TrialSourceControlBanner.messages';

export interface TrialSourceControlBannerProps {
  /** When 'fixed-bottom', positions the banner at the bottom of the screen like a toast (e.g. on new chat page). */
  position?: 'inline' | 'fixed-bottom';
  /** Ref to the chat input area element; when set, fixed-bottom banner is aligned to this element instead of viewport. */
  anchorRef?: RefObject<HTMLElement | null>;
  className?: string;
  'data-testid'?: string;
}

const fixedBottomWrapperClass = 'fixed bottom-4 z-50 px-4';
const fixedBottomFallbackClass =
  'left-1/2 w-full max-w-3xl -translate-x-1/2';

export function TrialSourceControlBanner({
  position = 'inline',
  anchorRef,
  className,
  'data-testid': testId = 'trial-source-control-banner',
}: TrialSourceControlBannerProps = {}) {
  const intl = useIntl();
  const { shouldShow, onTryNow, onDismiss } = useTrialSourceControlBanner();
  const [anchorRect, setAnchorRect] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (position !== 'fixed-bottom' || !anchorRef?.current) {
      setAnchorRect(null);
      return;
    }
    const el = anchorRef.current;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setAnchorRect({ left: rect.left, width: rect.width });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [position, anchorRef]);

  if (!shouldShow) return null;

  const alertContent = (
    <Banner
      role="banner"
      subVariant="pro"
      data-testid={testId}
      className={cn('shadow-sm', position === 'inline' && 'mb-3', className)}
    >
      <BannerContent>
        <BannerTitle>
          <FormattedMessage {...trialSourceControlBannerMessages.title} />
        </BannerTitle>
        <BannerDescription>
          <FormattedMessage {...trialSourceControlBannerMessages.description} />
        </BannerDescription>
      </BannerContent>
      <BannerAction>
        <Button
          variant="pro"
          size="xs"
          onClick={onTryNow}
          data-testid={`${testId}-try-now`}
        >
          <TierProFilled />
          <FormattedMessage {...trialSourceControlBannerMessages.tryNow} />
        </Button>
      </BannerAction>
      <BannerClose
        onClick={onDismiss}
        aria-label={intl.formatMessage(trialSourceControlBannerMessages.closeAriaLabel)}
        data-testid={`${testId}-close`}
      />
    </Banner>
  );

  if (position === 'fixed-bottom') {
    const style =
      anchorRect && anchorRect.width > 0
        ? { left: anchorRect.left, width: anchorRect.width }
        : undefined;
    if (anchorRef && !style) return null;
    return (
      <div
        className={cn(
          fixedBottomWrapperClass,
          !style && fixedBottomFallbackClass
        )}
        style={style}
      >
        {alertContent}
      </div>
    );
  }

  return alertContent;
}
